import { z } from 'zod';
import {
  isoDateSchema,
  longTextSchema,
  moneySchema,
  phoneSchema,
  shortTextSchema,
  uuidSchema,
} from '../primitives.js';
import { BIKE_OWNERSHIPS, HIRE_PURCHASE_STATUSES, RIDER_STATUSES } from '../enums.js';

/**
 * The rider directory.
 *
 * One row per rider, over the `riders` table that nine other tables already
 * reference. Everything on the right of the screen — current bike, tracker,
 * what they owe, how far through a hire purchase they are — is DERIVED from
 * those tables rather than copied here, so the directory cannot drift away from
 * the records it describes.
 *
 * ── Removing a rider ───────────────────────────────────────────────────────
 *
 * There is no delete. A rider with 366 deliveries, four accrued weeks and a
 * bike on instalments is the explanation for all of it, and removing the row
 * would strand every one. "Remove" deactivates: they leave the active list,
 * keep every record, and stop accruing remittance from a date somebody chose.
 */

/**
 * Registering a rider needs all three things a delivery shows about them.
 *
 * A rider registered without a Rider ID or phone is a rider the delivery form
 * cannot name, so neither is optional here. Riders registered before this
 * rule keep whatever their record holds; their gaps are filled by editing the
 * record (`riderUpdateSchema`), not by registering them again.
 */
export const riderInputSchema = z.object({
  canonicalName: shortTextSchema,
  /** The company's own code, e.g. CH-R008. Unique within the business. */
  externalId: z.string().trim().min(1, 'Rider ID is required').max(64),
  phone: phoneSchema,
  branchId: uuidSchema.nullish(),
  /** Null means not recorded. Never guessed from when the row was created. */
  joinedOn: isoDateSchema.nullish(),
  remittanceExempt: z.boolean().optional(),
  note: longTextSchema.nullish(),
});
export type RiderInput = z.infer<typeof riderInputSchema>;

export const riderUpdateSchema = riderInputSchema.partial().extend({
  /** Editing may leave either as it was, but not blank it out. */
  externalId: z.string().trim().min(1, 'Rider ID is required').max(64).optional(),
  phone: phoneSchema.optional(),
  /** `inactive` is not settable here — deactivation is its own operation. */
  status: z.enum(['active', 'suspended']).optional(),
});
export type RiderUpdate = z.infer<typeof riderUpdateSchema>;

/**
 * What a rider is still entangled with.
 *
 * Shown before deactivating, because a bike in somebody's hands, a tracker
 * still reporting and a half-paid agreement are things that need deciding
 * rather than silently unlinking.
 */
export const riderEntanglementsSchema = z.object({
  bikes: z.array(
    z.object({ id: uuidSchema, registration: z.string(), gpsImei: z.string().nullable() }),
  ),
  agreements: z.array(
    z.object({
      id: uuidSchema,
      bikeRegistration: z.string(),
      outstanding: moneySchema.nullable(),
      status: z.enum(HIRE_PURCHASE_STATUSES),
    }),
  ),
  /** Unpaid remittance. Not a blocker — it survives deactivation and stays owed. */
  outstandingRemittance: moneySchema.nullable(),
  /** True when nothing needs resolving first. */
  clear: z.boolean(),
  /**
   * Any delivery, charge, payment, settlement, assignment, agreement, loan or
   * clearance on record. A rider with history is archived on removal; one with
   * none is deleted.
   */
  hasHistory: z.boolean().optional(),
});
export type RiderEntanglements = z.infer<typeof riderEntanglementsSchema>;

export const deactivateRiderSchema = z.object({
  /** From when no further remittance accrues. */
  effectiveOn: isoDateSchema,
  reason: shortTextSchema,
  /**
   * The owner has seen the bike, tracker and agreement still attached and is
   * going ahead anyway, leaving them where they are.
   *
   * Required when anything is outstanding. Deactivating is not a reason to
   * quietly take a bike off somebody or cancel what they owe on it, so the
   * choice is made explicitly and recorded.
   */
  acknowledgeEntanglements: z.boolean().optional(),
});
export type DeactivateRiderInput = z.infer<typeof deactivateRiderSchema>;

export const riderDirectoryRowSchema = z.object({
  id: uuidSchema,
  canonicalName: z.string(),
  externalId: z.string().nullable(),
  phone: z.string().nullable(),
  branchId: uuidSchema.nullable(),
  branchName: z.string().nullable(),
  status: z.enum(RIDER_STATUSES),
  joinedOn: z.string().nullable(),

  /** Derived from `bikes.rider_id` — the register, not a copy. */
  bikeId: uuidSchema.nullable(),
  bikeRegistration: z.string().nullable(),
  /** Who owns the bike they hold, and the investor when it is one. */
  bikeOwnership: z.enum(BIKE_OWNERSHIPS).nullable().optional(),
  bikeInvestorName: z.string().nullable().optional(),
  gpsImei: z.string().nullable(),

  /** Derived from `rider_remittances`: what is accrued and still unpaid. */
  /** Null for a caller who may not see what riders owe. */
  remittanceOutstanding: moneySchema.nullable(),
  /** Derived from `hire_purchase_agreements` + its payments. */
  hirePurchaseStatus: z.enum(HIRE_PURCHASE_STATUSES).nullable(),
  hirePurchaseOutstanding: moneySchema.nullable(),

  deliveries: z.number().int(),
  remittanceExempt: z.boolean(),
  aliases: z.array(z.string()),
  deactivatedAt: z.string().nullable(),
  deactivationEffectiveOn: z.string().nullable(),
  deactivationReason: z.string().nullable(),
  note: z.string().nullable(),
  createdAt: z.string(),
});
export type RiderDirectoryRow = z.infer<typeof riderDirectoryRowSchema>;

export const riderFiltersSchema = z.object({
  /** `all` includes former riders; the default view does not. */
  status: z.enum(['active', 'suspended', 'inactive', 'all']).optional(),
  branchId: uuidSchema.optional(),
  search: z.string().trim().max(80).optional(),
  /** Who owns the bike the rider holds; `unassigned` for a rider holding none. */
  ownership: z.enum(['cenohub', 'investor', 'rider', 'other', 'unassigned']).optional(),
});

/** What happened when a rider was removed: deleted outright, or archived to keep their history. */
export const riderRemovalResultSchema = z.object({
  outcome: z.enum(['deleted', 'archived']),
});
export type RiderRemovalResult = z.infer<typeof riderRemovalResultSchema>;
export type RiderFilters = z.infer<typeof riderFiltersSchema>;

/**
 * A delivery name that resolves to no rider.
 *
 * Reported, never acted on. Two spellings differing by a letter can be two
 * people — CHIDUBAM against CHIDUBEM is 54 rides and a ₦15,000 exemption — so
 * nothing here is matched automatically, and the owner decides each one.
 */
export const unmatchedRiderNameSchema = z.object({
  riderName: z.string(),
  rides: z.number().int(),
  gross: moneySchema,
  firstSeen: z.string().nullable(),
  lastSeen: z.string().nullable(),
  /** A few delivery references, so the owner can go and look. */
  references: z.array(z.string()),
  /**
   * What this name would be owed if it were a rider. Always zero today:
   * accrual works from the roster, and an unmatched name is on no roster.
   */
  remittanceImpact: moneySchema,
});
export type UnmatchedRiderName = z.infer<typeof unmatchedRiderNameSchema>;

/** Resolve one unmatched name — deliberately, one at a time. */
export const resolveUnmatchedSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('link'), riderName: shortTextSchema, riderId: uuidSchema }),
  z.object({ action: z.literal('create'), riderName: shortTextSchema }),
  z.object({ action: z.literal('ignore'), riderName: shortTextSchema, reason: shortTextSchema }),
]);
export type ResolveUnmatchedInput = z.infer<typeof resolveUnmatchedSchema>;
